import React from "react"

// Hand-drawn pixel-art icons (16x16 bitmaps). "#" = filled pixel; rendered as
// crisp <rect>s in the current color so it inherits the tile's icon color.
//
// The grid is 16x16 and the tile paints it at 48px, so every pixel lands on an
// exact 3px square -- nothing lands on a half pixel and the edges stay hard.
// Strokes are 2px wide and diagonals step in even runs so the shapes read as
// deliberate pixel art rather than downsampled vectors.
//
// These are generic object motifs, not company marks: each row gets an icon
// that gestures at the work (a card for a bank, a drone for the robotics lab)
// rather than trying to redraw a logo at 16 pixels. The real logos live on the
// `logo` prop and only show up once a row is expanded.
const ICONS = {
    // --- job motifs ---
    card: [
        "................",
        "................",
        "................",
        "................",
        ".##############.",
        ".##..........##.",
        ".##############.",
        ".##############.",
        ".##..........##.",
        ".##.####.....##.",
        ".##.####.....##.",
        ".##..........##.",
        ".##############.",
        "................",
        "................",
        "................",
    ],
    cloud: [
        "................",
        "................",
        "................",
        "................",
        "........####....",
        "......########..",
        "..############..",
        ".##############.",
        ".##############.",
        ".##############.",
        ".##############.",
        "................",
        "................",
        "................",
        "................",
        "................",
    ],
    box: [
        "................",
        "................",
        "................",
        ".##############.",
        ".##..######..##.",
        ".##..######..##.",
        ".##############.",
        ".##..........##.",
        ".##..........##.",
        ".##..........##.",
        ".##..........##.",
        ".##..........##.",
        ".##############.",
        "................",
        "................",
        "................",
    ],
    robot: [
        "................",
        ".......##.......",
        ".......##.......",
        ".##############.",
        ".##..........##.",
        ".##.###..###.##.",
        ".##.###..###.##.",
        ".##..........##.",
        ".##.########.##.",
        ".##..........##.",
        ".##############.",
        "................",
        "................",
        "................",
        "................",
        "................",
    ],
    turtle: [
        "................",
        "................",
        "................",
        "..........####..",
        "..........####..",
        "...##########...",
        "..############..",
        ".##############.",
        ".##############.",
        "..############..",
        "...##########...",
        "..##......##....",
        "..##......##....",
        "................",
        "................",
        "................",
    ],
    shell: [
        "................",
        "................",
        ".....######.....",
        "...##########...",
        "..############..",
        ".####.####.####.",
        ".####.####.####.",
        ".##############.",
        ".####.####.####.",
        ".####.####.####.",
        "..############..",
        "...##########...",
        ".....######.....",
        "................",
        "................",
        "................",
    ],
    shoe: [
        "................",
        "................",
        "................",
        "................",
        "..........####..",
        "..........####..",
        ".......#######..",
        "....##########..",
        "..############..",
        "..############..",
        "..############..",
        ".##############.",
        ".##############.",
        "................",
        "................",
        "................",
    ],
    turbine: [
        "................",
        ".......##.......",
        ".......##.......",
        ".......##.......",
        ".##....##....##.",
        "..###..##..###..",
        "....########....",
        ".......##.......",
        ".......##.......",
        ".......##.......",
        ".......##.......",
        ".......##.......",
        ".......##.......",
        "......####......",
        ".....######.....",
        "................",
    ],
    drone: [
        "................",
        "................",
        ".####......####.",
        ".####......####.",
        "...##......##...",
        ".....######.....",
        "....########....",
        "....########....",
        ".....######.....",
        "...##......##...",
        ".####......####.",
        ".####......####.",
        "................",
        "................",
        "................",
        "................",
    ],
    leaf: [
        "................",
        "................",
        ".......##.......",
        "......####......",
        ".....##..##.....",
        "....##....##....",
        "...##..##..##...",
        "..##...##...##..",
        "..##...##...##..",
        "...##..##..##...",
        "....##.##.##....",
        ".....######.....",
        ".......##.......",
        ".......##.......",
        "................",
        "................",
    ],

    // --- project motifs ---
    note: [
        "................",
        "................",
        "........######..",
        "........######..",
        "........##..##..",
        "........##..##..",
        "........#####...",
        "........##......",
        "........##......",
        "........##......",
        "..########......",
        ".########.......",
        ".########.......",
        "..######........",
        "................",
        "................",
    ],
    chart: [
        "................",
        "................",
        ".##.............",
        ".##.............",
        ".##.........###.",
        ".##.........###.",
        ".##.....###.###.",
        ".##.....###.###.",
        ".##.###.###.###.",
        ".##.###.###.###.",
        ".##.###.###.###.",
        ".##.###.###.###.",
        ".##############.",
        ".##############.",
        "................",
        "................",
    ],
    trophy: [
        "................",
        "................",
        "..############..",
        ".##############.",
        "###..........###",
        "###..........###",
        ".##############.",
        "..############..",
        "...##########...",
        "....########....",
        "......####......",
        "......####......",
        "....########....",
        "..############..",
        "................",
        "................",
    ],
    pin: [
        "................",
        "....########....",
        "..############..",
        ".##############.",
        ".####......####.",
        ".###........###.",
        ".###........###.",
        ".####......####.",
        ".##############.",
        "..############..",
        "...##########...",
        ".....######.....",
        "......####......",
        ".......##.......",
        "................",
        "................",
    ],
    piano: [
        "................",
        "................",
        "................",
        "................",
        "################",
        "#.##.##..##.##.#",
        "#.##.##..##.##.#",
        "#.##.##..##.##.#",
        "#.##.##..##.##.#",
        "#.##.##..##.##.#",
        "#...#..#...#...#",
        "#...#..#...#...#",
        "#...#..#...#...#",
        "################",
        "................",
        "................",
    ],
    eye: [
        "................",
        "................",
        "................",
        "....########....",
        "..##........##..",
        ".##...####...##.",
        ".##..######..##.",
        ".##..######..##.",
        ".##...####...##.",
        "..##........##..",
        "....########....",
        "................",
        "................",
        "................",
        "................",
        "................",
    ],
    food: [
        "................",
        "................",
        "....########....",
        "..############..",
        ".##############.",
        ".##############.",
        "................",
        ".##############.",
        "................",
        ".##############.",
        ".##############.",
        "..############..",
        "....########....",
        "................",
        "................",
        "................",
    ],
    rocket: [
        "................",
        ".......##.......",
        "......####......",
        ".....######.....",
        ".....##..##.....",
        ".....##..##.....",
        ".....######.....",
        ".....######.....",
        "..############..",
        "..############..",
        "..###......###..",
        ".....######.....",
        "......####......",
        ".......##.......",
        "................",
        "................",
    ],
    camera: [
        "................",
        "................",
        "................",
        ".....####.......",
        ".##############.",
        ".##############.",
        ".####......####.",
        ".###..####..###.",
        ".###..####..###.",
        ".####......####.",
        ".##############.",
        ".##############.",
        "................",
        "................",
        "................",
        "................",
    ],
    satellite: [
        "................",
        "................",
        ".......##.......",
        "......####......",
        ".......##.......",
        ".###..####..###.",
        ".###..####..###.",
        ".###..####..###.",
        ".###..####..###.",
        ".......##.......",
        "................",
        "................",
        "................",
        "................",
        "................",
        "................",
    ],
    brain: [
        "................",
        "................",
        "....########....",
        "..############..",
        ".####..##..####.",
        ".##############.",
        ".####..##..####.",
        ".##############.",
        ".####..##..####.",
        "..############..",
        "....########....",
        "................",
        "................",
        "................",
        "................",
        "................",
    ],
    grip: [
        "................",
        "................",
        ".###.......###..",
        ".###.......###..",
        ".###.......###..",
        ".###.......###..",
        "..###.....###...",
        "...#########....",
        "....#######.....",
        ".....#####......",
        ".....#####......",
        ".....#####......",
        ".....#####......",
        ".....#####......",
        "................",
        "................",
    ],
    dollar: [
        "................",
        ".......##.......",
        "....########....",
        "...##..##..##...",
        "...##..##...##..",
        "...##..##.......",
        "....######......",
        "......######....",
        ".......##..##...",
        "..##...##..##...",
        "...##..##..##...",
        "....########....",
        ".......##.......",
        "................",
        "................",
        "................",
    ],
    bolt: [
        "................",
        "................",
        ".........####...",
        "........####....",
        ".......####.....",
        "......####......",
        "...##########...",
        "....########....",
        "......####......",
        ".....####.......",
        "....####........",
        "...####.........",
        "................",
        "................",
        "................",
        "................",
    ],
    droplet: [
        "................",
        "................",
        ".......##.......",
        "......####......",
        ".....######.....",
        "....########....",
        "...##########...",
        "..############..",
        "..############..",
        "..############..",
        "...##########...",
        "....########....",
        ".....######.....",
        "................",
        "................",
        "................",
    ],
    // --- education / credentials ---
    cap: [
        "................",
        "................",
        "................",
        ".......##.......",
        ".....######.....",
        "...##########...",
        ".##############.",
        "...##########...",
        "...##......##...",
        "...##......##...",
        "...##########...",
        "................",
        "................",
        "................",
        "................",
        "................",
    ],
    badge: [
        "................",
        "................",
        "....########....",
        "..############..",
        ".####......####.",
        ".###..####..###.",
        ".###..####..###.",
        ".####......####.",
        "..############..",
        "....########....",
        ".....##..##.....",
        "....##....##....",
        "...##......##...",
        "................",
        "................",
        "................",
    ],
    database: [
        "................",
        "................",
        "...##########...",
        "..############..",
        "..##........##..",
        "..############..",
        "..##........##..",
        "..##........##..",
        "..############..",
        "..##........##..",
        "..##........##..",
        "..############..",
        "...##########...",
        "................",
        "................",
        "................",
    ],
    book: [
        "................",
        "................",
        ".##############.",
        ".##############.",
        ".##..........##.",
        ".##.########.##.",
        ".##..........##.",
        ".##.########.##.",
        ".##..........##.",
        ".##.######...##.",
        ".##..........##.",
        ".##############.",
        ".##############.",
        "................",
        "................",
        "................",
    ],

    // --- about-section art (32x32) ---
    // Bigger canvas than the row icons above: these are display pieces, not
    // list bullets, so they get 32 pixels to work with and render at 128px
    // (exactly 4 device pixels per art pixel, so the edges stay hard).
    terminal: [
        "................................",
        "................................",
        "................................",
        "................................",
        "................................",
        "..hhhhhhhhhhhhhhhhhhhhhhhhhhhh..",
        "..hhhhhhhhhhhhhhhhhhhhhhhhhhhh..",
        "..hh........................hh..",
        "..hh.rr.aa.ll...............hh..",
        "..hh........................hh..",
        "..hhhhhhhhhhhhhhhhhhhhhhhhhhhh..",
        "..hh........................hh..",
        "..hh........................hh..",
        "..hh..gg......gggggg........hh..",
        "..hh....gg....gggggg........hh..",
        "..hh......gg..gggggg........hh..",
        "..hh....gg....gggggg........hh..",
        "..hh..gg......gggggg........hh..",
        "..hh........................hh..",
        "..hh........................hh..",
        "..hh..gggggggg...gggg.......hh..",
        "..hh........................hh..",
        "..hh..gggggg...gggggg.......hh..",
        "..hh........................hh..",
        "..hh........................hh..",
        "..hhhhhhhhhhhhhhhhhhhhhhhhhhhh..",
        "..hhhhhhhhhhhhhhhhhhhhhhhhhhhh..",
        "................................",
        "................................",
        "................................",
        "................................",
        "................................",
    ],
    briefcase: [
        "................................",
        "................................",
        "................................",
        "................................",
        "............yyyyyyyy............",
        "............yy....yy............",
        "............yy....yy............",
        "............yy....yy............",
        "............yy....yy............",
        "..bbbbbbbbbbbbbbbbbbbbbbbbbbbb..",
        "..bbbbbbbbbbbbbbbbbbbbbbbbbbbb..",
        "..bb........................bb..",
        "..bb........................bb..",
        "..bbbbbbbbbbbbbbbbbbbbbbbbbbbb..",
        "..bbbbbbbbbbbyyyyyybbbbbbbbbbb..",
        "..bbbbbbbbbbbyyyyyybbbbbbbbbbb..",
        "..bbbbbbbbbbbbbbbbbbbbbbbbbbbb..",
        "..bb........................bb..",
        "..bb........................bb..",
        "..bb........................bb..",
        "..bb........................bb..",
        "..bb........................bb..",
        "..bb........................bb..",
        "..bb........................bb..",
        "..bb........................bb..",
        "..bb........................bb..",
        "..bbbbbbbbbbbbbbbbbbbbbbbbbbbb..",
        "..bbbbbbbbbbbbbbbbbbbbbbbbbbbb..",
        "................................",
        "................................",
        "................................",
        "................................",
    ],
    drums: [
        "................................",
        "................................",
        "................................",
        ".....................yyyyyyyyyy.",
        "......................yyyyyyyy..",
        ".yyyyyyyyyy..............hh.....",
        "..yyyyyyyy...............hh.....",
        ".....hh..................hh.....",
        ".....hh..................hh.....",
        ".....hh..................hh.....",
        ".....hh.bbbbbbbb.bbbbbbbbhh.....",
        ".....hh.b......b.b......bhh.....",
        ".....hh.b......b.b......bhh.....",
        ".....hh.b......b.b......bhh.....",
        ".....hh.bbbbbbbb.bbbbbbbbhh.....",
        ".....hh..................hh.....",
        ".....hh..................hh.....",
        ".....hh.....bbbbbbbb.....hh.....",
        ".....hh....bb......bb....hh.....",
        ".....hh...bb........bb...hh.....",
        ".....hh..bb..........bb..hh.....",
        ".....hh..bb..........bb..hh.....",
        ".....hh..bb...yyyy...bb..hh.....",
        ".....hh..bb...yyyy...bb..hh.....",
        ".....hh..bb..........bb..hh.....",
        ".....hh..bb..........bb..hh.....",
        ".....hh..bb..........bb..hh.....",
        ".....hh...bb........bb...hh.....",
        ".....hh....bb......bb....hh.....",
        ".....hh.....bbbbbbbb.....hh.....",
        "...hhhhhh..............hhhhhh...",
        "................................",
    ],
    // --- social marks (home page quick links) ---
    // Unlike the motifs above, these are the real marks rather than gestures at
    // them: each grid is the official SVG rasterized onto a 20x20 pixel grid at a
    // half-coverage threshold, so the geometry is the brand's and only the
    // resolution is ours. 20 is about as coarse as they go -- at 16 the Octocat
    // loses its ears and reads as a plain ring. The cat and the LinkedIn "in" are
    // holes punched through a solid shape, exactly as those marks are drawn: the
    // page shows through them, and `depth` lets the extruded sides show inside.
    linkedin: [
        "..bbbbbbbbbbbbbbbb..",
        ".bbbbbbbbbbbbbbbbbb.",
        "bbbbbbbbbbbbbbbbbbbb",
        "bbbb..bbbbbbbbbbbbbb",
        "bbb...bbbbbbbbbbbbbb",
        "bbb...bbbbbbbbbbbbbb",
        "bbbbbbbbbbbbbbbbbbbb",
        "bbbbbbbbbbbbb.bbbbbb",
        "bbb...bb........bbbb",
        "bbb...bb........bbbb",
        "bbb...bb...bbb...bbb",
        "bbb...bb...bbb...bbb",
        "bbb...bb...bbb...bbb",
        "bbb...bb...bbb...bbb",
        "bbb...bb...bbb...bbb",
        "bbb...bb...bbb...bbb",
        "bbb...bb...bbb...bbb",
        "bbbbbbbbbbbbbbbbbbbb",
        ".bbbbbbbbbbbbbbbbbb.",
        "..bbbbbbbbbbbbbbbb..",
    ],
    github: [
        ".........ww.........",
        "......wwwwwwww......",
        "....wwwwwwwwwwww....",
        "...wwwwwwwwwwwwww...",
        "..wwwwwwwwwwwwwwww..",
        ".wwww..........wwww.",
        ".wwww..........wwww.",
        ".www...........wwww.",
        "wwww............wwww",
        "wwww............wwww",
        "wwww............wwww",
        "wwww............wwww",
        ".www............www.",
        ".wwww..........wwww.",
        ".ww.wwww....wwwwwww.",
        "..ww.www....wwwwww..",
        "...ww.......wwwww...",
        "....www.....wwww....",
        ".....www....www.....",
        "....................",
    ],
    kaggle: [
        "....................",
        "....bbb.............",
        "....bbb.............",
        "....bbb.............",
        "....bbb.............",
        "....bbb.............",
        "....bbb.............",
        "....bbb.....bbb.....",
        "....bbb....bbb......",
        "....bbb...bbb.......",
        "....bbb..bbb........",
        "....bbbbbbb.........",
        "....bbbbbb..........",
        "....bbbbbbb.........",
        "....bbbb.bbb........",
        "....bbb...bbb.......",
        "....bbb...bbb.......",
        "....bbb....bbb......",
        "....bbb.....bbb.....",
        "....................",
    ],
}

// The 16x16 motifs above draw every filled pixel as "#", which paints in
// currentColor so a row icon takes the tile's color. The about-section art is
// multi-colored instead: each glyph is a layer, and these say what each layer
// is. Anything without an entry here keeps inheriting currentColor as before.
export const PALETTES = {
    // h chrome hardware, g phosphor text, r/a/l the window dots
    terminal: { h: "#c9ced6", g: "#4ade80", r: "#ff5f57", a: "#febc2e", l: "#28c840" },
    // b leather, y brass
    briefcase: { b: "#8b5a2b", y: "#d9a441" },
    // b shell, y cymbal brass, h stands and pedal hardware
    drums: { b: "#8b5a2b", y: "#ffd200", h: "#c9ced6" },

    // Each social mark is one flat brand color -- the only shading is what the
    // extrusion adds, so the face itself stays true to the logo.
    linkedin: { b: "#0a66c2" },
    github: { w: "#f0f6fc" },
    kaggle: { b: "#20beff" },
}

// Side-face color for an extruded pixel: the same hue stepped down toward
// black, so the depth layers read as one solid block lit from the top left.
function shade(hex, factor) {
    const n = parseInt(hex.slice(1), 16)
    const channel = (shift) => Math.round(((n >> shift) & 255) * factor)
    const packed = (channel(16) << 16) | (channel(8) << 8) | channel(0)
    return `#${packed.toString(16).padStart(6, "0")}`
}

// `depth` extrudes the art: the same pixel runs are painted again behind the
// face, each copy shifted one pixel down-right and darkened, which turns a flat
// 16x16 sprite into a chunky block without leaving the pixel grid.
export default function PixelIcon({ name, className = "", colors, depth = 0 }) {
    const grid = ICONS[name]
    if (!grid) return null

    const palette = colors || PALETTES[name] || {}

    // Runs of the same glyph collapse into one <rect> so the SVG stays small and
    // adjacent pixels can never leave a hairline seam between them.
    const rects = []
    grid.forEach((row, y) => {
        let x = 0
        while (x < row.length) {
            const glyph = row[x]
            if (glyph === ".") {
                x++
                continue
            }
            let end = x
            while (end < row.length && row[end] === glyph) end++
            // "#" is left without a fill so it falls through to the <svg>'s
            // currentColor; every other glyph names a layer in the palette.
            rects.push(
                <rect
                    key={`${x}-${y}`}
                    x={x}
                    y={y}
                    width={end - x}
                    height="1"
                    fill={glyph === "#" ? undefined : palette[glyph]}
                />
            )
            x = end
        }
    })

    const size = grid.length

    // Farthest layer first so each nearer, lighter one paints over it. A "#"
    // pixel has no palette entry to darken, so its side face is shaded off
    // white -- depth is meant to pair with a palette.
    const sides = []
    for (let layer = depth; layer >= 1; layer--) {
        const factor = 0.3 + 0.18 * (1 - layer / Math.max(depth, 1))
        sides.push(
            <g key={`side-${layer}`} transform={`translate(${layer} ${layer})`}>
                {rects.map((rect) =>
                    React.cloneElement(rect, {
                        key: `${layer}-${rect.key}`,
                        fill: shade(rect.props.fill || "#ffffff", factor),
                    })
                )}
            </g>
        )
    }

    return (
        <svg
            viewBox={`0 0 ${size + depth} ${size + depth}`}
            className={className}
            fill="currentColor"
            shapeRendering="crispEdges"
            aria-hidden="true"
        >
            {sides}
            {rects}
        </svg>
    )
}
